/**
 * Timezone utility functions for proper date/time display
 */
import { normalizeApiTimestamp } from './ticketTimestamp';

// Sydney timezone identifier
export const SYDNEY_TIMEZONE = 'Australia/Sydney';

/**
 * Convert UTC string to Sydney timezone
 */
export function convertUtcToSydney(utcDateString: string): Date {
  const utcDate = new Date(utcDateString);
  return new Date(utcDate.toLocaleString('en-US', { timeZone: SYDNEY_TIMEZONE }));
}

/**
 * Format date in Sydney timezone
 */
export function formatSydneyTime(utcDateString: string, format: 'full' | 'date' | 'time' | 'relative' = 'full'): string {
  const timestamp = normalizeApiTimestamp(utcDateString);
  if (!timestamp) return 'Not available';
  const date = new Date(timestamp);
  
  switch (format) {
    case 'full':
      return date.toLocaleString('en-AU', { 
        timeZone: SYDNEY_TIMEZONE,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    
    case 'date':
      return date.toLocaleDateString('en-AU', { 
        timeZone: SYDNEY_TIMEZONE,
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    
    case 'time':
      return date.toLocaleTimeString('en-AU', { 
        timeZone: SYDNEY_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    
    case 'relative':
      return getRelativeTimeSydney(utcDateString);
    
    default:
      return formatSydneyTime(utcDateString, 'full');
  }
}

/**
 * Get relative time string in Sydney timezone (e.g., "2 hours ago")
 */
export function getRelativeTimeSydney(utcDateString: string): string {
  const timestamp = normalizeApiTimestamp(utcDateString);
  if (!timestamp) return 'Not available';
  const date = new Date(timestamp);
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  
  // Handle clock skew (if created in the near future by up to 1 minute)
  if (diffMs < 60 * 1000) {
    return 'Just now';
  }
  
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return formatSydneyTime(utcDateString, 'date');
  }
}

/**
 * Get current Sydney time
 */
export function getCurrentSydneyTime(): Date {
  const nowUtc = new Date();
  return new Date(nowUtc.toLocaleString('en-US', { timeZone: SYDNEY_TIMEZONE }));
}

/**
 * Check if it's business hours in Sydney (9 AM - 5 PM, Mon-Fri)
 */
export function isSydneyBusinessHours(): boolean {
  const now = getCurrentSydneyTime();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const hour = now.getHours();
  
  // Monday to Friday (1-5) and between 9 AM and 5 PM
  return dayOfWeek >= 1 && dayOfWeek <= 5 && hour >= 9 && hour < 17;
}

/**
 * Format timestamp with Sydney timezone indication
 */
export function formatWithTimezone(utcDateString: string, showTimezone: boolean = true): string {
  const formatted = formatSydneyTime(utcDateString, 'full');
  
  if (!showTimezone) {
    return formatted;
  }
  
  const now = getCurrentSydneyTime();
  const isDst = isAustralianDST(now);
  const timezoneAbbr = isDst ? 'AEDT' : 'AEST';
  
  return `${formatted} ${timezoneAbbr}`;
}

/**
 * Check if Australian Eastern Daylight Time (DST) is in effect
 */
function isAustralianDST(date: Date): boolean {
  const year = date.getFullYear();
  
  // DST in Australia runs from first Sunday in October to first Sunday in April
  const octStart = new Date(year, 9, 1); // October 1st
  const octFirstSunday = new Date(year, 9, 1 + (7 - octStart.getDay()) % 7);
  
  const aprStart = new Date(year, 3, 1); // April 1st  
  const aprFirstSunday = new Date(year, 3, 1 + (7 - aprStart.getDay()) % 7);
  
  const currentTime = date.getTime();
  
  // If we're between first Sunday of October and end of year, DST is active
  if (currentTime >= octFirstSunday.getTime()) {
    return true;
  }
  
  // If we're between start of year and first Sunday of April, DST is active
  if (currentTime < aprFirstSunday.getTime()) {
    return true;
  }
  
  return false;
}
